import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// A pet's care notes for groomers and vets (backend routes/pets.py): what the
// owner says about allergies, what upsets the pet, coat and grooming, health.
// A provider sees them only for its own booking; health notes only at a vet
// the owner agreed to share them with.

export const COAT_TYPES = [["single", "Single coat"], ["double", "Double coat"], ["curly", "Curly"], ["wire", "Wire"], ["hairless", "Hairless"], ["other", "Other"], ["unknown", "Not sure"]];
export const COAT_LENGTHS = [["short", "Short"], ["medium", "Medium"], ["long", "Long"], ["unknown", "Not sure"]];
export const MATTING = [["none", "None"], ["some", "Some"], ["heavy", "A lot"], ["unknown", "Not sure"]];
const BLANK = { allergies: "", health_notes: "", triggers: [], other_triggers: "", behaviour_notes: "", coat_type: "", coat_length: "",
                matting: "", grooming_notes: "", past_reactions: "" };

/** One line for a pet card: what a provider most needs to know. */
export function careSummary(care) {
  if (!care) return "";
  const bits = [];
  if (care.allergies) bits.push(`Allergies: ${care.allergies}`);
  const n = (care.triggers || []).length + (care.other_triggers ? 1 : 0);
  if (n) bits.push(`${n} thing${n === 1 ? "" : "s"} that upset them`);
  if (care.coat_type && care.coat_type !== "unknown") bits.push(`${(COAT_TYPES.find(([v]) => v === care.coat_type) || [])[1]}`);
  return bits.join(" · ");
}

function Pills({ options, value, onChange, multi, testid }) {
  const on = (v) => (multi ? value.includes(v) : value === v);
  const toggle = (v) => onChange(multi ? (on(v) ? value.filter((x) => x !== v) : [...value, v]) : (value === v ? "" : v));
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => toggle(v)} aria-pressed={on(v)} data-testid={`${testid}-${v}`}
          className={`px-3 h-8 rounded-full border text-xs font-medium ${on(v) ? "bg-stoneWarm text-white border-stoneWarm" : "bg-white border-stone-300 text-stone-700"}`}>{l}</button>
      ))}
    </div>
  );
}

export default function PetCareForm({ pet, onSaved, onClose }) {
  const [f, setF] = useState(() => ({ ...BLANK, ...Object.fromEntries(Object.entries(pet.care || {}).map(([k, v]) => [k, v ?? (k === "triggers" ? [] : "")])) }));
  const [triggers, setTriggers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/pets/care-options").then(({ data }) => setTriggers((data.triggers || []).map((t) => [t.value, t.label])), () => {}); }, []);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.keys(BLANK).map((k) => [k, k === "triggers" ? f.triggers : (f[k] || null)]));
      const { data } = await api.put(`/customer/pets/${pet.id}/care`, body);
      toast.success(`${pet.name}'s care notes saved`);
      onSaved?.(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 text-sm" data-testid="pet-care-form">
      <p className="text-stone-600">Groomers and vets see these only when you book with them, so the visit goes well. This is what you tell them, not a diagnosis.</p>
      <div><Label>Allergies</Label><Input value={f.allergies} onChange={set("allergies")} maxLength={300} placeholder="e.g. chicken, some shampoos" className="mt-1" data-testid="care-allergies" /></div>
      <div><Label>What upsets {pet.name}?</Label>
        <div className="mt-1"><Pills options={triggers} value={f.triggers} onChange={(v) => setF((x) => ({ ...x, triggers: v }))} multi testid="care-triggers" /></div>
        <Input value={f.other_triggers} onChange={set("other_triggers")} maxLength={200} placeholder="Anything else (optional)" className="mt-2" /></div>
      <div><Label>Handling tips</Label><Textarea value={f.behaviour_notes} onChange={set("behaviour_notes")} maxLength={500} rows={2} placeholder="e.g. calm with treats, needs a muzzle for nails" className="mt-1" /></div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-3"><Label>Coat</Label><div className="mt-1"><Pills options={COAT_TYPES} value={f.coat_type} onChange={(v) => setF((x) => ({ ...x, coat_type: v }))} testid="care-coat" /></div></div>
        <div className="sm:col-span-2"><Label>Coat length</Label><div className="mt-1"><Pills options={COAT_LENGTHS} value={f.coat_length} onChange={(v) => setF((x) => ({ ...x, coat_length: v }))} testid="care-length" /></div></div>
        <div><Label>Matting</Label><div className="mt-1"><Pills options={MATTING} value={f.matting} onChange={(v) => setF((x) => ({ ...x, matting: v }))} testid="care-matting" /></div></div>
      </div>
      <div><Label>Grooming preferences</Label><Textarea value={f.grooming_notes} onChange={set("grooming_notes")} maxLength={500} rows={2} placeholder="e.g. short summer cut, no perfume" className="mt-1" /></div>
      <div><Label>Bad reactions before</Label><Input value={f.past_reactions} onChange={set("past_reactions")} maxLength={300} placeholder="e.g. rash after a medicated shampoo" className="mt-1" /></div>
      <div><Label>Health notes <span className="font-normal text-stone-500">(only vets you allow see this)</span></Label>
        <Textarea value={f.health_notes} onChange={set("health_notes")} maxLength={800} rows={2} placeholder="Conditions, medicines, skin problems…" className="mt-1" data-testid="care-health" /></div>
      <div className="flex justify-end gap-2">
        {onClose && <Button variant="outline" className="rounded-full" onClick={onClose}>Cancel</Button>}
        <Button onClick={save} disabled={busy} className="rounded-full bg-stoneWarm text-white" data-testid="care-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save care notes"}</Button>
      </div>
    </div>
  );
}
